<template>
  <div class="lobby w-[100lvw] h-[100lvh] bg-cw-orange flex flex-col">
    <div class="bg-cw-dark-alt/80 h-screen w-screen absolute z-0 inset-0"></div>
    <div class="flex z-1 p-[30px]">
      <!-- LEFT SIDEBAR TABS -->
      <div class="flex-3">
        <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" @click="closeSettings">
          <ArrowFatLeftIcon />
        </BaseButton>

        <div class="flex flex-col gap-y-1 mt-8">
          <BaseButton v-for="tab in tabs" :key="tab" background-color="transparent" border-color="transparent" class="px-1 opacity-50" :class="{ 'opacity-100': activeTab === tab }" @click="setActiveTab(tab)">
            <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold">{{ tab }}</p>
            <PlayIcon class="ml-2" v-if="activeTab === tab" />
          </BaseButton>
        </div>
      </div>

      <!-- MAIN CONTENT -->
      <div class="flex-6 flex pt-[10vh]">
        <!-- ACCOUNT SETTINGS -->
        <div v-if="activeTab === 'Account'" class="min-w-[400px] w-full bg-cw-dark rounded-[13px] px-9 md:px-10 pt-8 md:pt-8 pb-8 md:pb-10 bounce-in mx-auto max-h-[70vh] overflow-auto">
          <div class="grid grid-cols-1 gap-y-5">
            <div class="input-group mb-6">
              <Carousel v-bind="carouselConfig" v-model="avatar">
                <Slide v-for="(avatarOption, index) in avatarOptions" :key="avatarOption">
                  <img :src="avatarOption" :class="['w-24 h-24 rounded-2xl cursor-pointer border-4 border-[#082552]']" @click="selectAvatar(index)" />
                </Slide>
              </Carousel>
              <div class="input-label text-center text-[#4D4D4D] mt-2">Swipe to choose an avatar</div>
            </div>

            <div class="flex flex-col gap-y-5">
              <div class="flex flex-row items-center justify-between py-1 h-[40px]">
                <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">Name</p>
                <div class="text-right cursor-pointer" @click="toggleEditName">
                  <div v-if="!editingName" class="flex items-center gap-2">
                    <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold">{{ editedFullName }}</p>
                  </div>
                  <div v-else class="input-group">
                    <input
                      v-model="editedFullName"
                      ref="fullNameInput"
                      class="input-item !text-sm md:!text-base xl:!text-xl lg:text-base font-bold"
                      placeholder="First Name"
                      @keyup.enter="saveNameChanges"
                      @blur="saveNameChanges"
                    />
                  </div>

                  <template v-if="$v.editedFullName.$dirty">
                    <span v-for="error in $v.editedFullName.$errors" :key="error.$uid" class="input-error text-cw-red">
                      {{ error.$message }}
                    </span>
                  </template>
                </div>
              </div>

              <div class="flex flex-row items-center justify-between py-1 h-[40px]">
                <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">Email</p>
                <div class="text-right cursor-pointer" @click="toggleEditEmail">
                  <div v-if="!editingEmail" class="flex items-center gap-2">
                    <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold" v-if="editedEmail">{{ editedEmail }}</p>

                    <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-semibold underline inline-flex items-center text-cw-green" v-else>
                      Add your email
                      <ArrowSlideRightIcon class="w-auto h-4 ml-2" />
                    </p>
                  </div>
                  <div v-else class="input-group">
                    <input
                      v-model="editedEmail"
                      ref="emailInput"
                      type="email"
                      class="input-item !text-sm md:!text-base xl:!text-xl lg:text-base font-bold"
                      placeholder="email@example.com"
                      @keyup.enter="saveEmailChanges"
                      @blur="saveEmailChanges"
                    />
                  </div>
                  <template v-if="$v.editedEmail.$dirty">
                    <span v-for="error in $v.editedEmail.$errors" :key="error.$uid" class="input-error text-cw-red">
                      {{ error.$message }}
                    </span>
                  </template>
                </div>
              </div>

              <div class="flex flex-row items-center justify-between py-1 h-[40px]">
                <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">Username</p>
                <div class="text-right cursor-default opacity-40">
                  <div class="flex items-center gap-2">
                    <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold">{{ user?.username }}</p>
                  </div>
                </div>
              </div>

              <div class="flex flex-row items-center justify-between py-1 h-[40px]">
                <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">Date of Birth</p>
                <div class="text-right cursor-pointer" @click="toggleEditDateOfBirth">
                  <div v-if="!editingDateOfBirth" class="flex items-center gap-2">
                    <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold">
                      {{
                        new Date(editedDateOfBirth || '').toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      }}
                    </p>
                  </div>
                  <div v-else class="input-group">
                    <Datepicker
                      v-model="editedDateOfBirth"
                      model-type="yyyy-MM-dd"
                      :format="'yyyy-MM-dd'"
                      :max-date="fiveYearsAgo"
                      :start-date="fiveYearsAgo"
                      focus-start-date
                      :enable-time-picker="false"
                      position="left"
                      auto-position="top"
                      auto-apply
                      :ui="{
                        input: 'input-item !text-sm md:!text-base xl:!text-xl lg:text-base font-bold bg-red-500'
                      }"
                      name="dob"
                      :teleport-center="useHelpers().isMobileDevice()"
                      @blur="saveDateOfBirthChanges"
                      @closed="saveDateOfBirthChanges"
                    />
                  </div>
                  <template v-if="$v.editedDateOfBirth.$dirty">
                    <span v-for="error in $v.editedDateOfBirth.$errors" :key="error.$uid" class="input-error text-cw-red">
                      {{ error.$message }}
                    </span>
                  </template>
                </div>
              </div>

              <div class="flex flex-row items-center justify-between py-1 h-[40px]">
                <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">School</p>
                <div class="text-right cursor-pointer">
                  <div v-if="!editingSchool" class="flex items-center gap-2" @click="toggleEditSchool">
                    <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold" v-if="selectedSchoolName">{{ selectedSchoolName }}</p>

                    <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-semibold underline inline-flex items-center text-cw-green" v-else>
                      Add your school
                      <ArrowSlideRightIcon class="w-auto h-4 ml-2" />
                    </p>
                  </div>
                  <div v-else class="input-group">
                    <select v-model="editedSchool" ref="schoolInput" class="input-item !text-sm md:!text-base xl:!text-xl lg:text-base font-bold" @change="saveSchoolChanges" @blur="saveSchoolChanges">
                      <option :value="null">Select your school</option>
                      <option :value="school.id" :key="school.id" v-for="school in schools">{{ school.name }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SECURITY SETTINGS -->
        <div v-if="activeTab === 'Security'" class="min-w-[400px] w-full bg-cw-dark rounded-[13px] px-9 md:px-10 pt-8 md:pt-8 pb-8 md:pb-10 bounce-in mx-auto max-h-[70vh] overflow-auto">
          <div class="flex flex-col gap-y-5">
            <div class="flex flex-row items-center justify-between py-1">
              <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">Reset password</p>
              <BaseButton class="px-6" background-color="var(--color-cw-indigo)" border-color="#1F2547" @click="resetPassword">
                <p class="!text-sm md:!text-base xl:!text-xl">Reset password</p>
              </BaseButton>
            </div>

            <div class="flex flex-row items-center justify-between py-1">
              <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">Sign out of your account</p>
              <BaseButton class="px-6" background-color="var(--color-cw-olive)" border-color="#323617" @click="showSignOutModal = true">
                <p class="!text-sm md:!text-base xl:!text-xl">Sign out</p>
              </BaseButton>
            </div>

            <div class="flex flex-row items-center justify-between py-1">
              <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold text-[#999999]">Delete account</p>
              <BaseButton class="px-6" background-color="var(--color-cw-red-light)" border-color="#471F20" @click="showDeleteAccountModal = true">
                <p class="!text-sm md:!text-base xl:!text-xl">Delete your account</p>
              </BaseButton>
            </div>
          </div>
        </div>
      </div>

      <!-- ACTIONS -->
      <div class="flex-3 flex justify-end h-full">
        <div v-if="activeTab === 'Account' && !isProfileUnedited" class="actions bounce-in">
          <BaseButton class="px-6" background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" @click="updateProfile()">
            <Loader v-if="isLoading" />
            <p class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold">Save changes</p>
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- DELETE ACCOUNT MODAL -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div class="flex items-center justify-center fixed inset-0 z-1" v-if="showDeleteAccountModal">
        <div class="bg-cw-dark-alt/80 h-screen w-screen absolute z-1 inset-0" @click.stop="showDeleteAccountModal = false"></div>
        <div class="flex items-start justify-center relative z-2 bounce-in">
          <div class="relative max-w-120 bg-cw-dark rounded-[13px] px-10 pt-10 pb-12">
            <div class="mt-1">
              <div class="pt-3 heightMd:pt-2">
                <h3 class="text-left text-white mb-7 heightMd:mb-5 font-bold tracking-normal heightMd:text-lg text-2xl">Delete account</h3>
                <p class="text-left mb-7 heightMd:mb-5 font-normal tracking-normal heightMd:text-sm text-[16px] text-[#999999]">Are you sure you want to delete your account? This action cannot be undone.</p>
                <p v-if="errorMessage" class="text-cw-red">{{ errorMessage }}</p>
                <div class="flex justify-start items-center gap-3">
                  <BaseButton
                    @click="deleteAccount"
                    backgroundColor="var(--color-cw-green)"
                    borderColor="var(--color-cw-green-border-shadow)"
                    class="font-bold text-lg heightMd:text-[16px] px-8 py-4"
                    :class="{ 'px-12': !isLoading }"
                  >
                    <Loader v-if="isLoading" />
                    Yes, Delete
                  </BaseButton>
                  <BaseButton @click="showDeleteAccountModal = false" backgroundColor="var(--color-cw-red-light)" borderColor="#471F20" class="font-bold text-lg heightMd:text-[16px] px-16 py-4">
                    Cancel
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- SIGN OUT MODAL -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div class="flex items-center justify-center fixed inset-0 z-1" v-if="showSignOutModal">
        <div class="bg-cw-dark-alt/80 h-screen w-screen absolute z-1 inset-0" @click.stop="showSignOutModal = false"></div>
        <div class="flex items-start justify-center relative z-2 bounce-in">
          <div class="relative max-w-120 bg-cw-dark rounded-[13px] px-10 pt-10 pb-12">
            <div class="mt-1">
              <div class="pt-3 heightMd:pt-2">
                <h3 class="text-left text-white mb-7 heightMd:mb-5 font-bold tracking-normal heightMd:text-lg text-2xl">Sign Out</h3>
                <p class="text-left mb-7 heightMd:mb-5 font-normal tracking-normal heightMd:text-sm text-[16px] text-[#999999]">Are you sure you want to sign out?</p>
                <div class="flex justify-start items-center gap-3">
                  <BaseButton @click="logout" backgroundColor="var(--color-cw-green)" borderColor="var(--color-cw-green-border-shadow)" class="font-bold text-lg heightMd:text-[16px] px-10 py-4">
                    Yes, Sign Out
                  </BaseButton>
                  <BaseButton @click="showSignOutModal = false" backgroundColor="var(--color-cw-red-light)" borderColor="#471F20" class="font-bold text-lg heightMd:text-[16px] px-14 py-4">Cancel</BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- UPDATE PASSWORD -->
    <UpdatePasswordModal ref="updatePasswordModalRef" />
  </div>
</template>

<script setup lang="ts">
import 'vue3-carousel/carousel.css'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useHelpers } from '@/composables/useHelpers'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import ArrowFatLeftIcon from '@/assets/svgs/icons/arrow-fat-left.svg'
import PlayIcon from '@/assets/svgs/icons/play-icon.svg'
import BaseButton from '@/components/base/BaseButton.vue'
import UpdatePasswordModal from '@/components/modals/auth/UpdatePasswordModal.vue'
import Loader from '@/components/interface/Loader.vue'
import { Carousel, Slide } from 'vue3-carousel'
import useSocket from '@/composables/useSocket'
import { useRouter } from 'vue-router'
import UserService from '@/services/api/user-service'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { useUser } from '@/composables/useUser'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import ArrowSlideRightIcon from '@/assets/svgs/icons/arrow-slide-right.svg'

const { fetchAllSchools } = useUser()
fetchAllSchools()

const { showSettings, schools } = storeToRefs(useAppStore())
const { user } = storeToRefs(useUserStore())
const router = useRouter()
const { showToast } = useHelpers()
const socket = useSocket()

const carouselConfig = {
  itemsToShow: 4.5,
  wrapAround: true
}
const avatarOptions = [
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-g.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-h.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-i.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-j.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-k.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-a.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-b.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-c.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-d.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-e.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-f.svg?alt=media'
]

const avatar = ref<number>(user.value?.avatarUrl ? avatarOptions.indexOf(user.value?.avatarUrl) : 0)
const showDeleteAccountModal = ref(false)
const showSignOutModal = ref(false)
const isLoading = ref(false)
const tabs = ['Account', 'Security']
const activeTab = ref(tabs[0])
const setActiveTab = (tab: string) => {
  activeTab.value = tab
}
const updatePasswordModal = useTemplateRef('updatePasswordModalRef')
const fullNameInput = useTemplateRef('fullNameInput')
const schoolInput = useTemplateRef('schoolInput')
const emailInput = useTemplateRef('emailInput')
const errorMessage = ref('')
const editingName = ref(false)
const editedFullName = ref(`${user.value?.firstName} ${user.value?.lastName}` || '')
const editingDateOfBirth = ref(false)
const editedDateOfBirth = ref(user.value?.dateOfBirth || '')
const editingSchool = ref(false)
const editedSchool = ref(user.value?.school?.id || null)
const editingEmail = ref(false)
const editedEmail = ref(user.value?.email || '')

const isProfileUnedited = computed(
  () =>
    editedFullName.value === `${user.value?.firstName} ${user.value?.lastName}` &&
    editedDateOfBirth.value === user.value?.dateOfBirth &&
    avatarOptions[avatar.value] === user.value?.avatarUrl &&
    editedSchool.value === user.value?.school?.id &&
    editedEmail.value === user.value?.email
)

const fiveYearsAgo = computed(() => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 5)
  return date
})

const selectedSchoolName = computed(() => {
  return schools.value.find(school => school.id === editedSchool.value)?.name || ''
})

const $v = useVuelidate(
  {
    editedFullName: {
      required: helpers.withMessage('Enter your full name', required)
    },
    editedDateOfBirth: {
      required: helpers.withMessage('Select your date of birth', required)
    },
    editedEmail: {
      email: helpers.withMessage('Enter a valid email address', (value: string) => {
        if (!value) return true // email is not required
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(value)
      })
    }
  },
  { editedFullName, editedDateOfBirth, editedEmail },
  { $scope: false, $stopPropagation: true }
)

const toggleEditName = () => {
  editingName.value = !editingName.value
  if (editingName.value) {
    setTimeout(() => {
      fullNameInput.value?.focus()
    }, 100)
  }
}

const toggleEditDateOfBirth = () => {
  editingDateOfBirth.value = !editingDateOfBirth.value
  if (!editingDateOfBirth.value) {
    editedDateOfBirth.value = user.value?.dateOfBirth || ''
  }
}

const saveNameChanges = () => {
  editingName.value = false

  if (!editedFullName.value) {
    editedFullName.value = `${user.value?.firstName} ${user.value?.lastName}` || ''
  }
}

const saveDateOfBirthChanges = () => {
  editingDateOfBirth.value = false
}

const toggleEditSchool = () => {
  editingSchool.value = !editingSchool.value
  if (editingSchool.value) {
    setTimeout(() => {
      schoolInput.value?.focus()
    }, 100)
  }
}

const saveSchoolChanges = () => {
  editingSchool.value = false
}

const toggleEditEmail = () => {
  editingEmail.value = !editingEmail.value
  if (editingEmail.value) {
    setTimeout(() => {
      emailInput.value?.focus()
    }, 100)
  }
}

const saveEmailChanges = async () => {
  const isValid = await $v.value.editedEmail.$validate()

  if (!isValid) {
    return
  }
  editingEmail.value = false

  if (!editedEmail.value) {
    editedEmail.value = user.value?.email || ''
  }
}

const resetPassword = () => {
  updatePasswordModal.value?.open()
}

const logout = () => {
  socket.disconnect()
  closeSettings()
  router.push('/logout')
}

const selectAvatar = (index: number) => {
  avatar.value = index
}

const updateProfile = async () => {
  const isValid = await $v.value.$validate()

  if (!isValid) {
    return
  }
  if (!user.value) return
  try {
    isLoading.value = true
    const { data } = await UserService.updateProfile({
      avatarUrl: avatarOptions[avatar.value],
      firstName: editedFullName.value.split(' ')[0] || user.value?.firstName || '',
      lastName: editedFullName.value.split(' ')[1] || user.value?.lastName || '',
      dateOfBirth: editedDateOfBirth.value || user.value?.dateOfBirth || '',
      email: editedEmail.value || '',
      schoolId: editedSchool.value || ''
    })
    showToast('Profile updated successfully.')
    useUserStore().setUser(data)
  } catch (error: any) {
    errorMessage.value = error?.data?.message || 'An error occurred. Please try again.'
  } finally {
    isLoading.value = false
  }
}

const deleteAccount = async () => {
  try {
    isLoading.value = true
    await UserService.deleteAccount()
    showToast('You have successfully deleted your account.')
    logout()
  } catch (error: any) {
    errorMessage.value = error?.data?.message || 'An error occurred. Please try again.'
  } finally {
    isLoading.value = false
  }
}

const closeSettings = () => {
  showSettings.value = false
}

watch(user, (newValue, oldValue) => {
  if (!oldValue && newValue) {
    // Reset edited fields when user data is loaded
    editedFullName.value = `${newValue.firstName} ${newValue.lastName}`
    editedDateOfBirth.value = newValue.dateOfBirth || ''
    avatar.value = newValue.avatarUrl ? avatarOptions.indexOf(newValue.avatarUrl) : 0
    editedSchool.value = newValue.school?.id || null
    editedEmail.value = newValue.email || ''
  }
})
</script>

<style></style>
